import { useState, useMemo, Fragment } from "react";
import { Search, ExternalLink, Mail, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { vendors, VENDOR_CATEGORY_ORDER } from "@/data/vendors";

export function VendorDirectory() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return vendors;
    const q = query.toLowerCase();
    return vendors.filter((v) =>
      v.vendor.toLowerCase().includes(q) || v.service.toLowerCase().includes(q) ||
      v.contactName.toLowerCase().includes(q) || v.category.toLowerCase().includes(q));
  }, [query]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((v) => { (g[v.category] = g[v.category] || []).push(v); });
    return g;
  }, [filtered]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="module-content">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Vendor Directory</h2>
        <p className="text-muted-foreground">Find approved marketing vendors and their contact information</p>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input type="text" placeholder="Search by vendor, service, or category..." value={query}
          data-testid="vendor-search" onChange={(e) => setQuery(e.target.value)} className="pl-10" />
      </div>
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Service</TableHead>
              <TableHead className="w-[180px]">Vendor</TableHead>
              <TableHead className="w-[150px]">Contact</TableHead>
              <TableHead className="w-[220px]">Email</TableHead>
              <TableHead className="w-[130px]">Phone</TableHead>
              <TableHead>Website</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {VENDOR_CATEGORY_ORDER.map((cat) => {
              const list = grouped[cat];
              if (!list || list.length === 0) return null;
              return (
                <Fragment key={`cat-${cat}`}>
                  <TableRow key={`c-${cat}`} className="bg-muted/50 hover:bg-muted/50">
                    <TableCell colSpan={6} className="py-3"><Badge variant="secondary" className="text-sm">{cat}</Badge></TableCell>
                  </TableRow>
                  {list.map((v) => (
                    <TableRow key={v.id} data-testid={`vendor-${v.id}`}>
                      <TableCell className="font-medium">{v.service}</TableCell>
                      <TableCell>{v.vendor}</TableCell>
                      <TableCell>{v.contactName || "—"}</TableCell>
                      <TableCell>{v.email ? <a href={`mailto:${v.email}`} className="flex items-center gap-1.5 text-primary hover:underline"><Mail className="h-3.5 w-3.5 shrink-0" /><span className="text-sm break-all">{v.email}</span></a> : "—"}</TableCell>
                      <TableCell>{v.phoneNumber ? <a href={`tel:${v.phoneNumber.replace(/\./g, "")}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"><Phone className="h-3.5 w-3.5 shrink-0" /><span className="text-sm">{v.phoneNumber}</span></a> : "—"}</TableCell>
                      <TableCell>{v.website ? <a href={v.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-primary hover:underline"><ExternalLink className="h-3.5 w-3.5" /><span className="text-sm">Visit</span></a> : "—"}</TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              );
            })}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="vendor-empty">No vendors found matching "{query}"</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="text-sm text-muted-foreground">Showing {filtered.length} of {vendors.length} vendors</div>
    </div>
  );
}
